/**
 * 動画生成の所要時間予測 (進捗バー用)。
 *
 * バックエンド (backend/services/video_eta_service.py) が実測値
 * (generation_metadata.generation_seconds) を集計したテーブルを取得し、
 * (model, duration) から所要時間 (秒) を推定する。
 *
 * 推定ロジックはバックエンドの estimate_seconds と一致させること。
 * 詳細・運用手順: docs/features/video-generation-eta.md
 */
import { apiClient } from './apiClient';

export interface VideoEtaModelEntry {
  method: 'linear' | 'bucket' | 'model_median';
  n: number;
  overhead?: number;
  slope?: number;
  buckets: Record<string, number>;
  model_median?: number;
}

export interface VideoEtaTable {
  models: Record<string, VideoEtaModelEntry>;
  defaults: Record<string, number>; // { seedance, veo, other }
  min_sec: number;
  max_sec: number;
  updated_at: string;
}

function familyOf(model: string): string {
  const m = (model || '').toLowerCase();
  if (m.includes('seedance')) return 'seedance';
  if (m.includes('veo')) return 'veo';
  return 'other';
}

export async function fetchVideoEtaTable(): Promise<VideoEtaTable> {
  const { data } = await apiClient.get<VideoEtaTable>('/generation/video-eta');
  return data;
}

/**
 * (model, duration) に対する予測所要秒数。バックエンド estimate_seconds と同一ロジック。
 * テーブル未取得 (table=null) の場合は系統別の保守的既定値にフォールバックする。
 */
export function estimateVideoSec(
  table: VideoEtaTable | null | undefined,
  model: string | undefined,
  duration: number | undefined,
): number | null {
  if (!model) return null;
  const defaults = table?.defaults ?? { seedance: 240, veo: 90, other: 120 };
  const minSec = table?.min_sec ?? 20;
  const maxSec = table?.max_sec ?? 900;

  const entry = table?.models?.[model];
  let est: number | null = null;
  if (entry) {
    if (entry.method === 'linear' && entry.overhead != null && entry.slope != null && duration != null) {
      est = entry.overhead + entry.slope * duration;
    }
    if (est == null && duration != null) {
      const key = Number.isInteger(duration) ? String(duration) : String(duration);
      const b = entry.buckets?.[key];
      if (b != null) est = b;
    }
    if (est == null && entry.model_median != null) {
      est = entry.model_median;
    }
  }
  if (est == null) {
    est = defaults[familyOf(model)] ?? defaults.other ?? 120;
  }
  return Math.round(Math.max(minSec, Math.min(maxSec, est)));
}
